<script>
import { computed, reactive } from 'vue';
import { isCcMappingSupported } from '@midulate/shared';
import Effect from './Effect.vue';
import PatchGlobals from './PatchGlobals.vue';
import PatchList from './PatchList.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { library } from '@fortawesome/fontawesome-svg-core'
import { faTrash, faSquarePlus, faSliders, faCaretDown } from '@fortawesome/free-solid-svg-icons'

library.add(faTrash, faSquarePlus, faSliders, faCaretDown);

export default {
  components: {
    Effect,
    PatchGlobals,
    PatchList,
    FontAwesomeIcon
  },

  data() {
    return {
      dragSrcIndex: null,
      dragOverIndex: null,
      // Incoming-CC mapping overlay (MDL-267). Provided to CcBadge in the
      // effect forms/shells; deliberately not persisted — always opens
      // inactive on patch load.
      ccOverlay: { active: false },
    }
  },

  provide() {
    return {
      ccOverlay: this.ccOverlay,
      ccPatchEffects: computed(() => this.patch.effects),
    };
  },

  inject: {
    // Mute/solo state for the active patch, owned by App.vue so that it
    // outlives this component — PatchForm unmounts on every tab change, and
    // the device keeps muting regardless. The default keeps the component
    // mountable on its own.
    muteState: { default: () => reactive({ muted: [], soloed: [] }) },
  },

  props: {
    index: {
      type: Number,
      required: true
    },

    patch: {
      type: Object,
      required: true
    },

    patches: {
      type: Array,
      required: true
    },

    devices: {
      type: Array,
      required: true
    },

    connectedDevice: {
      type: Object,
      required: false,
      default: null
    },

    connectedToDevice: {
      type: Boolean,
      required: true,
      default: false
    },

    tempoInfo: {
      type: Object,
      required: false,
      default: null
    },

    capabilities: {
      type: Object,
      required: false,
      default: () => ({ capabilities: {} })
    }
  },

  methods: {
    // Esc closes the Incoming MIDI overlay when it's active. When an
    // assignment modal is open, let it handle Esc first (native dialog) so
    // the first press closes just the modal, the next closes the overlay.
    // Blur whatever holds focus (typically the toggle button) so it doesn't
    // keep a focus ring after the overlay closes.
    onOverlayKeydown(e) {
      if (e.key !== 'Escape' || !this.ccOverlay.active) return;
      if (document.querySelector('dialog.cc-assign-modal[open]')) return;
      this.ccOverlay.active = false;
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    },

    allEffectIndexes() {
      return this.patch.effects.map((e, i) => i);
    },

    deletePatch() {
      this.$emit('deletePatch', this.index);
    },

    deleteEffect(patchIndex, effectIndex) {
      this.$emit('deleteEffect', patchIndex, effectIndex);
    },

    mutedEffectsIfAnySoloed() {
      if (this.soloedEffects.length > 0) {
        return this.allEffectIndexes().filter(i => !this.soloedEffects.includes(i));
      } else {
        return this.mutedEffects;
      }
    },

    muteEffectsOnDevice() {
      if (!this.connectedDevice) {
        return;
      }

      this.connectedDevice.setMutedEffects(this.mutedEffectsIfAnySoloed());
    },

    showDeleteModal() {
      const modal = document.getElementById('delete_patch_' + this.index);
      if (modal) {
        modal.showModal();
      }
    },

    toggleMute(effectIndex) {
      if (this.soloedEffects.length > 0) {
        // When solos are active, muting is disabled
        return;
      }

      const idx = this.mutedEffects.indexOf(effectIndex);
      if (idx === -1) {
        this.mutedEffects.push(effectIndex);
      } else {
        this.mutedEffects.splice(idx, 1);
      }

      this.muteEffectsOnDevice();
    },

    toggleSolo(effectIndex) {
      const idx = this.soloedEffects.indexOf(effectIndex);
      if (idx === -1) {
        this.soloedEffects.push(effectIndex);
      } else {
        this.soloedEffects.splice(idx, 1);
      }

      if (this.soloedEffects.length > 0) {
        // When at least one solo is active, mute all non-soloed effects
        this.mutedEffects = this.allEffectIndexes().filter(i => !this.soloedEffects.includes(i));
      } else {
        // When no solos are active, unmute all effects
        this.mutedEffects = [];
      }

      this.muteEffectsOnDevice();
    },

    updateEffect(effectArgs) {
      this.$emit('updateEffect', effectArgs);
    },

    updatePatch(newPatch) {
      this.$emit('updatePatch', this.index, newPatch);
    },

    onDragStart(effectIndex, event) {
      this.dragSrcIndex = effectIndex;
      event.dataTransfer.effectAllowed = 'move';
      // Needed for Firefox
      event.dataTransfer.setData('text/plain', effectIndex);
    },

    onDragEnter(effectIndex) {
      if (effectIndex !== this.dragSrcIndex) {
        this.dragOverIndex = effectIndex;
      }
    },

    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },

    onDrop(effectIndex) {
      if (this.dragSrcIndex === null || this.dragSrcIndex === effectIndex) return;
      const effects = this.patch.effects;
      const moved = effects.splice(this.dragSrcIndex, 1)[0];
      effects.splice(effectIndex, 0, moved);
      this.$emit('reorderEffects', this.index, effects);
      this.dragSrcIndex = null;
      this.dragOverIndex = null;
    },

    onDragEnd() {
      this.dragSrcIndex = null;
      this.dragOverIndex = null;
    },

    // ── Mega-menu popover positioning fallback ─────────────────────────────
    // daisyUI's megamenu anchors its popover panel with CSS anchor positioning,
    // which is Chromium-only today. Where it is unsupported (Firefox, older
    // Safari) a native popover just centres in the viewport, so we position it
    // under its trigger ourselves. In supporting browsers this is a no-op —
    // daisyUI's anchoring stays in charge and we set no inline styles.
    installPopoverFallback() {
      const anchorSupported =
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('anchor-name: --a');
      if (anchorSupported) return;

      const panelEl = document.getElementById('patch-panel');
      const triggerEl = this.$el.querySelector('[popovertarget="patch-panel"]');
      if (!panelEl || !triggerEl) return;

      this.popoverToggleHandler = (e) => {
        if (e.newState === 'open') {
          this.positionPopover(panelEl, triggerEl);
          this.popoverReposition = () => this.positionPopover(panelEl, triggerEl);
          window.addEventListener('resize', this.popoverReposition);
          window.addEventListener('scroll', this.popoverReposition, true);
        } else if (this.popoverReposition) {
          window.removeEventListener('resize', this.popoverReposition);
          window.removeEventListener('scroll', this.popoverReposition, true);
          this.popoverReposition = null;
        }
      };
      panelEl.addEventListener('toggle', this.popoverToggleHandler);
      this.popoverPanelEl = panelEl;
    },

    positionPopover(panelEl, triggerEl) {
      const gap = 4;
      const margin = 8;
      const r = triggerEl.getBoundingClientRect();
      panelEl.style.position = 'fixed';
      panelEl.style.margin = '0';
      panelEl.style.inset = 'auto';
      panelEl.style.top = `${r.bottom + gap}px`;
      panelEl.style.left = `${r.left}px`;
      // Clamp to the viewport once the panel has a measured size.
      requestAnimationFrame(() => {
        const pr = panelEl.getBoundingClientRect();
        if (pr.right > window.innerWidth - margin) {
          panelEl.style.left = `${Math.max(margin, window.innerWidth - margin - pr.width)}px`;
        }
        if (pr.bottom > window.innerHeight - margin) {
          panelEl.style.top = `${Math.max(margin, r.top - gap - pr.height)}px`;
        }
      });
    },

    removePopoverFallback() {
      if (this.popoverPanelEl && this.popoverToggleHandler) {
        this.popoverPanelEl.removeEventListener('toggle', this.popoverToggleHandler);
      }
      if (this.popoverReposition) {
        window.removeEventListener('resize', this.popoverReposition);
        window.removeEventListener('scroll', this.popoverReposition, true);
        this.popoverReposition = null;
      }
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onOverlayKeydown);
    this.installPopoverFallback();
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onOverlayKeydown);
    this.removePopoverFallback();
  },

  watch: {
    // A different patch object means a patch load/switch — overlay resets.
    patch() {
      this.ccOverlay.active = false;
    },
  },

  computed: {
    // Read/write views onto the shared state, so the mute and solo logic below
    // reads the same as when these were local data.
    mutedEffects: {
      get() { return this.muteState.muted; },
      set(value) { this.muteState.muted = value; },
    },

    soloedEffects: {
      get() { return this.muteState.soloed; },
      set(value) { this.muteState.soloed = value; },
    },

    ccMappingSupported() {
      return isCcMappingSupported(this.capabilities);
    },

    sortedEffects() {
      return this.patch.effects.slice().sort((a, b) => {
        if (a.channel > b.channel) return 1;
        if (a.channel < b.channel) return -1;
        // if (a.cc > b.cc) return 1;
        // if (a.cc < b.cc) return -1;
        return 0;
      });
    }
  }
}
</script>
<template>
  <div class="card w-full rounded-none overflow-visible">
    <div class="card-body">
      <div class="flex items-center gap-3 flex-wrap mb-2">
        <!-- Patch title doubles as the mega-menu trigger (daisyUI megamenu). -->
        <nav class="megamenu" id="patch-megamenu">
          <span class="megamenu-active"></span>
          <button
            popovertarget="patch-panel"
            class="btn btn-ghost h-auto min-h-0 py-1 px-2 -ml-2 gap-2 normal-case font-normal"
            aria-label="Switch patch"
          >
            <span class="card-title text-3xl mb-0">{{ patch.title }}</span>
            <FontAwesomeIcon :icon="['fas', 'caret-down']" class="text-base opacity-60" />
          </button>
          <div
            id="patch-panel"
            popover
            class="bg-base-100 border border-base-300 rounded-box shadow-lg p-3"
          >
            <div class="w-72 max-w-[90vw]">
              <div class="text-xs font-semibold uppercase tracking-wide opacity-60 px-2 pb-2">
                Patches
              </div>
              <PatchList
                :patches="patches"
                :currentPatch="index"
                @selectPatch="$emit('selectPatch', $event)"
                @reorderSelect="$emit('reorderSelect', $event)"
              />
            </div>
          </div>
        </nav>

        <span class="badge badge-neutral badge-ghost">
          <span class="text-gray-500 dark:text-gray-100/50">PC:</span> {{ index }}
        </span>

        <div class="flex-1"></div>

        <button class="btn btn-sm btn-ghost" @click="$emit('addPatch')">
          <FontAwesomeIcon :icon="['fas', 'square-plus']" /> Add Patch
        </button>

        <button
          class="btn btn-sm btn-ghost hover:btn-error"
          :disabled="patches.length <= 1"
          @click="showDeleteModal()"
        >
          <FontAwesomeIcon :icon="['fas', 'trash']" /> Delete Patch
        </button>

        <dialog :id="'delete_patch_' + this.index" class="modal">
          <div class="modal-box">
            <h3 class="text-lg font-bold">Are you sure?</h3>
            <p class="py-4">This will delete the patch <em>{{ patch.title }}</em>.</p>
            <div class="modal-action">
              <form method="dialog">
                <button class="btn btn-ghost">Cancel</button>
                <button class="btn btn-ghost btn-error" @click="deletePatch">
                  <FontAwesomeIcon :icon="['fas', 'trash']" /> Yes, delete it
                </button>
              </form>
            </div>
          </div>
        </dialog>
      </div>

      <PatchGlobals
        :patch="patch"
        :tempoInfo="tempoInfo"
        :capabilities="capabilities"
        @update:patch="updatePatch"
      />

      <div class="flex items-center gap-3 flex-wrap mb-8 py-1 px-6 -mx-6 sticky top-10 z-[5] bg-base-100">
        <h3 class="text-2xl">Effects</h3>

        <div class="flex-1"></div>

        <button
          v-if="ccMappingSupported"
          class="btn btn-sm tooltip tooltip-left"
          :class="{ 'btn-active btn-primary': ccOverlay.active }"
          data-tip="Assign incoming CC messages to effect parameters"
          @click="ccOverlay.active = !ccOverlay.active"
        >
          <FontAwesomeIcon :icon="['fas', 'sliders']" /> Incoming MIDI
        </button>

        <button class="btn btn-sm" @click="$emit('addEffect', index)">
          <FontAwesomeIcon :icon="['fas', 'square-plus']" /> Add Effect
        </button>
      </div>

      <ul class="w-full flex flex-wrap gap-6 mb-4">
        <li
          v-for="(effect, effectIndex) in patch.effects"
          :key="effect.uuid || effectIndex"
          :class="{ 'effects-drop-target': dragOverIndex === effectIndex }"
          draggable="false"
          @dragover="onDragOver"
          @dragenter.prevent="onDragEnter(effectIndex)"
          @drop.prevent="onDrop(effectIndex)"
        >
          <Effect
            :index="index + ':' + effectIndex"
            :effectIndex="effectIndex"
            :patchIndex="index"
            :effect="effect"
            :meter="patch.meter ?? 4"
            :devices="devices"
            :mute="mutedEffects.includes(effectIndex)"
            :solo="soloedEffects.includes(effectIndex)"
            :connectedToDevice="connectedToDevice"
            :dragging="dragSrcIndex === effectIndex"
            :dragHandleListeners="{
              dragstart: (e) => onDragStart(effectIndex, e),
              dragend: onDragEnd,
            }"
            @deleteEffect="deleteEffect"
            @toggleMute="toggleMute"
            @toggleSolo="toggleSolo"
            @updateEffect="updateEffect"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
/* daisyUI's .megamenu draws its own CSS chevron via [popovertarget]::after.
   We render an explicit caret-down icon to match the navbar dropdowns, so
   suppress the built-in one to avoid a duplicate arrow. */
#patch-megamenu [popovertarget]::after {
  display: none;
}
</style>